import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Save, RotateCcw, Loader2, Info, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_LABELS = {
  appartement: 'Appartement',
  maison: 'Maison / Villa',
  immeuble: 'Immeuble',
  terrain: 'Terrain',
  local_commercial: 'Local Commercial',
  bureau: 'Bureau',
  entrepot: 'Entrepôt',
  parking: 'Parking',
};

export default function PartnerEstimationCoefsTab() {
  const [coefs, setCoefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Vue coefficients state
  const [vueCats, setVueCats] = useState([]);
  const [editingVue, setEditingVue] = useState(null);
  const [savingVue, setSavingVue] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('partner_token')}` });

  const fetchCoefs = async () => {
    setLoading(true);
    try {
      const [coefsRes, vueRes] = await Promise.all([
        axios.get(`${API}/api/partenaire/estimation-coefficients`, { headers: getHeaders() }),
        axios.get(`${API}/api/partenaire/vue-coefficients`, { headers: getHeaders() }),
      ]);
      setCoefs(coefsRes.data.coefficients || []);
      setVueCats(vueRes.data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des coefficients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoefs(); }, []);

  const toggleActive = async (coef) => {
    try {
      await axios.put(`${API}/api/partenaire/estimation-coefficients/${coef.id}`,
        { active: !coef.active },
        { headers: getHeaders() }
      );
      setCoefs(prev => prev.map(c => c.id === coef.id ? { ...c, active: !c.active } : c));
      toast.success(`${coef.label} ${!coef.active ? 'activé' : 'désactivé'}`);
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const saveRow = async () => {
    if (!editingRow) return;
    setSaving(true);
    try {
      const payload = { coef: parseFloat(editingRow.coef) };
      await axios.put(`${API}/api/partenaire/estimation-coefficients/${editingRow.id}`, payload, {
        headers: getHeaders()
      });
      setCoefs(prev => prev.map(c => c.id === editingRow.id ? { ...c, ...payload } : c));
      setEditingRow(null);
      toast.success('Modifications enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Remettre les coefficients par défaut ? Vos modifications personnalisées seront supprimées.')) return;
    try {
      await axios.post(`${API}/api/partenaire/estimation-coefficients/reset-defaults`, {}, { headers: getHeaders() });
      await fetchCoefs();
      toast.success('Coefficients réinitialisés');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const startEditVue = (cat) => {
    setEditingVue({ catId: cat.id, subs: (cat.subs || []).map(s => ({ ...s })) });
  };

  const saveVue = async () => {
    if (!editingVue) return;
    setSavingVue(true);
    try {
      await axios.put(`${API}/api/partenaire/vue-coefficients/${editingVue.catId}`,
        { subs: editingVue.subs },
        { headers: getHeaders() }
      );
      setVueCats(prev => prev.map(c => c.id === editingVue.catId ? { ...c, subs: editingVue.subs } : c));
      setEditingVue(null);
      toast.success('Coefficients Vue mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingVue(false);
    }
  };

  const resetVueDefaults = async () => {
    if (!window.confirm('Remettre les coefficients Vue par défaut ?')) return;
    try {
      await axios.post(`${API}/api/partenaire/vue-coefficients/reset`, {}, { headers: getHeaders() });
      await fetchCoefs();
      toast.success('Coefficients Vue réinitialisés');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const grouped = coefs.reduce((acc, c) => {
    (acc[c.property_type] = acc[c.property_type] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="partner-estimation-coefs-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-navy">Coefficients d'Estimation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Modifiez l'impact de chaque caractéristique sur le prix d'estimation.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resetDefaults}
          className="border-red-300 text-red-700 hover:bg-red-50"
          data-testid="partner-reset-coefs-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Remettre par défaut
        </Button>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Comment ça marche :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Coef %</strong> : impact sur le prix final (ex : 5 = +5%, -15 = -15%)</li>
              <li><strong>Case à cocher</strong> : le client coche ou non (ex : Rénové)</li>
              <li><strong>Quantité</strong> : le client saisit un nombre (ex : 2 parkings → coef × 2)</li>
              <li>Désactiver une caractéristique la masque du formulaire client</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {Object.keys(PROPERTY_TYPE_LABELS).map(pt => (
        <Card key={pt} data-testid={`partner-coefs-${pt}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-navy text-white rounded-t-lg">
              <h3 className="font-bold">{PROPERTY_TYPE_LABELS[pt]}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-xs">
                  <tr>
                    <th className="px-3 py-2 text-left w-10">Icône</th>
                    <th className="px-3 py-2 text-left">Libellé (client)</th>
                    <th className="px-3 py-2 text-center w-24">Coef %</th>
                    <th className="px-3 py-2 text-left w-36">Type d'input</th>
                    <th className="px-3 py-2 text-center w-20">Actif</th>
                    <th className="px-3 py-2 text-center w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped[pt] || []).map(coef => (
                    <tr key={coef.id} className={`border-t ${!coef.active ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2 text-xl">{coef.icon || '—'}</td>
                      <td className="px-3 py-2 font-medium">
                        {coef.label}
                        {coef.has_override && <span className="ml-2 text-xs text-amber-600 font-normal">(modifié)</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {editingRow?.id === coef.id ? (
                          <Input
                            type="number"
                            step="0.5"
                            value={editingRow.coef}
                            onChange={e => setEditingRow(r => ({ ...r, coef: e.target.value }))}
                            className="w-20 text-center h-8 text-sm mx-auto"
                          />
                        ) : (
                          <span className={`font-mono font-bold ${coef.coef > 0 ? 'text-green-600' : coef.coef < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {coef.coef > 0 ? '+' : ''}{coef.coef}%
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs">
                        {coef.input_type === 'quantity' ? 'Quantité' : coef.input_type === 'surface' ? 'Surface m²' : 'Case à cocher'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch checked={coef.active} onCheckedChange={() => toggleActive(coef)} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {editingRow?.id === coef.id ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" onClick={saveRow} disabled={saving} className="h-7 px-2 text-xs">
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingRow(null)} className="h-7 px-2 text-xs">✕</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setEditingRow({ ...coef })} className="h-7 px-3 text-xs">
                            Modifier
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(grouped[pt] || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400 text-xs italic">Aucune caractéristique</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Vue Coefficients Section */}
      <div className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-navy">Coefficients Vue</h3>
          </div>
          <Button variant="outline" size="sm" onClick={resetVueDefaults} className="border-red-300 text-red-700 hover:bg-red-50 text-xs">
            <RotateCcw className="w-3 h-3 mr-1" /> Réinitialiser Vue
          </Button>
        </div>

        {vueCats.map(cat => (
          <Card key={cat.id} className="mb-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800">{cat.category_label || cat.label}</h4>
                {editingVue?.catId === cat.id ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveVue} disabled={savingVue} className="text-xs h-7">
                      {savingVue ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                      Enregistrer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingVue(null)} className="text-xs h-7">Annuler</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => startEditVue(cat)} className="text-xs h-7">Modifier</Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(editingVue?.catId === cat.id ? editingVue.subs : cat.subs || []).map((sub, idx) => (
                  <div key={sub.sub_id || sub.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span className="text-gray-700">{sub.sub_label || sub.label}</span>
                    {editingVue?.catId === cat.id ? (
                      <Input
                        type="number"
                        step="0.5"
                        value={sub.coef}
                        onChange={e => setEditingVue(ev => ({
                          ...ev,
                          subs: ev.subs.map((s, i) => i === idx ? { ...s, coef: parseFloat(e.target.value) || 0 } : s)
                        }))}
                        className="w-20 h-6 text-xs text-center"
                      />
                    ) : (
                      <span className={`font-mono text-xs font-bold ${sub.coef > 0 ? 'text-green-600' : sub.coef < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {sub.coef > 0 ? '+' : ''}{sub.coef}%
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
